import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { heroSlides, eventCountdownTarget } from '../mock';
import Countdown from './Countdown';

const HeroSlideshow = () => {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const single = heroSlides.length <= 1;

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % heroSlides.length);
  }, []);
  const prev = () => setCurrent((c) => (c - 1 + heroSlides.length) % heroSlides.length);

  useEffect(() => {
    if (paused || single) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, paused, single]);

  return (
    <section
      className={`relative w-full overflow-hidden ${single ? 'min-h-screen' : 'h-screen min-h-[640px]'}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          style={{ transitionDuration: '1200ms' }}
          className={`${single ? 'relative min-h-screen' : 'absolute inset-0'} transition-opacity ease-in-out ${
            i === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {slide.type === 'poster' ? (
            <>
              {/* Blurred background of the poster */}
              <div
                className="absolute inset-0 bg-cover bg-center scale-110 blur-2xl opacity-60"
                style={{ backgroundImage: `url(${slide.image})` }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#0f2c1f]/80 via-[#0f2c1f]/60 to-[#0f2c1f]/90" />
              <div className="relative z-20 min-h-screen flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12 px-5 max-w-6xl mx-auto pt-24 pb-16 md:py-24">
                {i === current && (
                  <>
                    <img
                      src={slide.image}
                      alt="KST Spice Corner 15 Years Grand Opening"
                      className="animate-fade-up max-h-[38vh] sm:max-h-[46vh] md:max-h-[72vh] w-auto rounded-2xl shadow-2xl object-contain"
                    />
                    <div className="text-center md:text-left max-w-md">
                      <p className="animate-fade-up text-[#dcbf74] text-xs md:text-sm font-semibold tracking-[0.35em] mb-4">
                        {slide.eyebrow}
                      </p>
                      <h1
                        className="animate-fade-up font-serif-display text-white text-3xl md:text-5xl font-bold leading-tight text-shadow-lg mb-6"
                        style={{ animationDelay: '0.15s' }}
                      >
                        <span className="gold-shimmer italic">{slide.titleGold}</span>
                      </h1>
                      <div className="animate-fade-up mb-7 flex justify-center md:justify-start" style={{ animationDelay: '0.25s' }}>
                        <Countdown target={eventCountdownTarget} label={slide.countdownLabel || 'Coming in'} />
                      </div>
                      <div
                        className="animate-fade-up flex flex-col sm:flex-row gap-4 justify-center md:justify-start"
                        style={{ animationDelay: '0.4s' }}
                      >
                        <Link
                          to={slide.primary.to}
                          className="group inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-[#c8a24c] text-[#0f2c1f] font-semibold hover:bg-[#dcbf74] transition-all duration-300 shadow-lg"
                        >
                          {slide.primary.label}
                          <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <Link
                          to={slide.secondary.to}
                          className="inline-flex items-center justify-center px-8 py-3.5 rounded-full border-2 border-white/70 text-white font-semibold hover:bg-white hover:text-[#0f2c1f] transition-all duration-300"
                        >
                          {slide.secondary.label}
                        </Link>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <>
              <div
                className={`absolute inset-0 bg-cover bg-center ${i === current ? 'slide-zoom' : ''}`}
                style={{ backgroundImage: `url(${slide.image})` }}
              />
              {/* Overlay for readability */}
              <div className="absolute inset-0 bg-gradient-to-b from-[#0f2c1f]/85 via-[#0f2c1f]/55 to-[#0f2c1f]/90" />
              <div className="absolute inset-0 bg-black/25" />

              <div className="relative z-20 h-full flex flex-col items-center justify-center text-center px-5 max-w-5xl mx-auto">
                {i === current && (
                  <>
                    {slide.badge && (
                      <span className="animate-fade-up mb-5 inline-block px-4 py-1.5 rounded-full border border-[#c8a24c] text-[#c8a24c] text-xs font-semibold tracking-[0.2em] bg-[#0f2c1f]/40">
                        {slide.badge}
                      </span>
                    )}
                    <p
                      className="animate-fade-up text-[#dcbf74] text-xs md:text-sm font-semibold tracking-[0.35em] mb-5"
                      style={{ animationDelay: '0.1s' }}
                    >
                      {slide.eyebrow}
                    </p>
                    <h1
                      className="animate-fade-up font-serif-display text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] text-shadow-lg mb-6"
                      style={{ animationDelay: '0.2s' }}
                    >
                      {slide.titleLead}{' '}
                      <span className="gold-shimmer italic">{slide.titleGold}</span>{' '}
                      {slide.titleTail}
                    </h1>
                    <p
                      className="animate-fade-up text-white/85 text-base md:text-lg max-w-2xl leading-relaxed mb-9"
                      style={{ animationDelay: '0.3s' }}
                    >
                      {slide.subtitle}
                    </p>
                    <div
                      className="animate-fade-up flex flex-col sm:flex-row gap-4"
                      style={{ animationDelay: '0.4s' }}
                    >
                      <Link
                        to={slide.primary.to}
                        className="group inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-[#c8a24c] text-[#0f2c1f] font-semibold hover:bg-[#dcbf74] transition-all duration-300 shadow-lg"
                      >
                        {slide.primary.label}
                        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                      </Link>
                      <Link
                        to={slide.secondary.to}
                        className="inline-flex items-center justify-center px-8 py-3.5 rounded-full border-2 border-white/70 text-white font-semibold hover:bg-white hover:text-[#0f2c1f] transition-all duration-300"
                      >
                        {slide.secondary.label}
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      ))}

      {/* Arrows */}
      {!single && (
        <>
          <button
            onClick={prev}
            aria-label="Previous slide"
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full bg-black/30 border border-white/30 text-white flex items-center justify-center hover:bg-[#c8a24c] hover:text-[#0f2c1f] hover:border-[#c8a24c] transition-all duration-300"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            onClick={next}
            aria-label="Next slide"
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-30 w-11 h-11 rounded-full bg-black/30 border border-white/30 text-white flex items-center justify-center hover:bg-[#c8a24c] hover:text-[#0f2c1f] hover:border-[#c8a24c] transition-all duration-300"
          >
            <ChevronRight size={22} />
          </button>

          {/* Dots */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-3">
            {heroSlides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-400 ${
                  i === current ? 'w-10 bg-[#c8a24c]' : 'w-2 bg-white/50 hover:bg-white/80'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

export default HeroSlideshow;
